/* eslint-disable no-nested-ternary */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  CircularProgress,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  SelectChangeEvent,
  Tooltip,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import RadioButton from "Components/Form/RadioGroup";
import SkeletonComponent from "Components/Generic/Skeleton";
import { add, differenceInDays, isBefore, isSameDay, startOfDay, sub } from "date-fns";
import { zonedTimeToUtc, format } from "date-fns-tz";
import TaskDateUtils from "Util/dateUtils";
import { Member } from "Models/members/member";
import { Team } from "Models/team";
import { useTranslation } from "react-i18next";
import { IoArrowUndoSharp } from "react-icons/io5";

import { getEquipmentName } from "Util/getEquipmentName";
import { getMemberDescription } from "Util/getMemberDescription";
import {
  getCompleteDateRangeLabel,
  getStartDatePeriods,
  getStartDateRangeLabel,
} from "Util/startDate";
import * as yup from "yup";

import { Input, Select } from "../../../../../../../components/Form";
import AutoSuggest from "../../../../../../../components/Form/AutoSuggest";
import { Color } from "../../../../../../../components/Form/Button";
import FieldView from "../../../../../../../components/Form/FieldView";
import ActionButtons from "../../../../../../../components/Generic/ActionButtons";
import BadgeJobStatus from "../../../../../../../components/Generic/BadgeJobStatus";
import GeneralCommentsComponent from "../../../../../../../components/Generic/GeneralComments";
import { NotificationToast } from "../../../../../../../components/Generic/Toast/NotificationToast";
import { useAppState } from "../../../../../../../context/StateContext";
import useCurrentBrand from "../../../../../../../hooks/useCurrentBrand";
import useYupValidation from "../../../../../../../hooks/useYupValidation";
import { Error } from "../../../../../../../models/error";
import { Fleet } from "../../../../../../../models/fleet";
import { Staff } from "../../../../../../../models/staff";
import { Task, TaskStartDatePeriod } from "../../../../../../../models/task";
import { TaskComment } from "../../../../../../../models/task.comment";
import { TaskType } from "../../../../../../../models/task.type";
import { User } from "../../../../../../../models/user";
import SendEmailService from "../../../../../../../services/send.email.service";
import { checkError } from "../../../../../../../util/checkError";
import { convertMetricVolume } from "../../../../../../../util/converts";
import {
  TaskAutoSuggestInputType,
  TaskStatus,
  TaskTypeRole,
  TimeSlotType,
} from "../../../../../../../util/enums";
import { getMeasurement } from "../../../../../../../util/getMeasurement";
import { isAllied } from "../../../../../../../util/isAllied";
import { MenuItemTitle, PeriodMenuItem } from "../styles";
import useStyles, {
  ButtonsContainer,
  GeneralWrapper,
  HeaderActions,
  MilestoneButtonContent,
  MultiDayTaskBadge,
  OpenInfoSection,
  TaskItemContainer,
  TaskItemHeader,
  TaskStatusMenuItem,
  TaskStatusMenuTitle,
  TaskTitle,
  TitleAndStatusContainer,
} from "./styles";

import TaskViewMode from "./ViewMode";
import {
  TimeControlAndExpenseModal,
  TimeControlAndExpensePropsHandles,
} from "Components/TimeControlModal";
import { VariableSlotTime } from "../VariableSlotTime";
import { TimeSlotTask } from "Models/time.slot.task";
import { Share } from "iconsax-react";
import { colors } from "@zapalacorp/tokens";
import { MOVING_AGENT_COMPANY_PROFILE_ID } from "Constants";

type LoadingTypes = {
  isLoadingTaskTypeList: boolean;
  isLoadingTeams: boolean;
  isLoadingMembers: boolean;
  isLoadingFleets: boolean;
};

type Props = {
  task: Task;
  onUpdate: (task: Task) => void;
  isOnViewMode?: boolean;
  expandAll?: boolean;
  fleets?: Fleet[];
  staffs?: Staff[];
  members?: Member[];
  memberLeaders?: Member[];
  leaders?: User[];
  teams?: Team[];
  taskTypeSequence?: number;
  tasksTypesList?: TaskType[];
  isLoadingData: LoadingTypes;
};

const ARROW_DOWN_ICON = require("../../../../../../../assets/icons/arrow-down.svg");
const ARROW_SWAP_ICON = require("../../../../../../../assets/icons/arrow-swap-horizontal.svg");
const ARROW_UP_ICON = require("../../../../../../../assets/icons/arrow-up.svg");
const CLOCK_ICON = require("../../../../../../../assets/icons/clock.svg");
const CLOSE_CIRCLE_ICON = require("../../../../../../../assets/icons/close-circle.svg");
const EDIT_ICON = require("../../../../../../../assets/icons/edit.svg");
const SEND_ICON = require("../../../../../../../assets/icons/send-2.svg");
const CORNOR_UP_RIGHT_ICON = require("../../../../../../../assets/img/corner-up-right.svg");
const SIGN_POST_3_ICON = require("../../../../../../../assets/img/signpost-3.svg");
const SIGN_POST_2_ICON = require("../../../../../../../assets/img/signpost-2.svg");
const SIGN_POST_ICON = require("../../../../../../../assets/img/signpost.svg");

const PERIOD_BUFFER_TIME = {
  [TaskStartDatePeriod.MORNING]: { hours: 6 },
  [TaskStartDatePeriod.AFTERNOON]: { hours: 6 },
  [TaskStartDatePeriod.EVENING]: { hours: 6 },
  [TaskStartDatePeriod.FULL_DAY]: { hours: 12 },
  [TaskStartDatePeriod.CUSTOM]: { hours: 1 },
  [TaskStartDatePeriod.MULTI_DAY]: { days: 1 },
  [TaskStartDatePeriod.VARIABLE_TIME]: { hours: 1 },
  [TaskStartDatePeriod.FIXED_TIME]: { hours: 1 },
};

const TaskItem = (props: Props) => {
  const {
    task,
    isOnViewMode = false,
    expandAll = false,
    onUpdate,
    fleets,
    members,
    memberLeaders,
    teams,
    taskTypeSequence,
    tasksTypesList,
    isLoadingData,
  } = props;

  const { t: translate } = useTranslation();
  const { currentUser, defaultTimezone, userTimezone, agentPermissions } = useAppState();
  const timezone = userTimezone?.name || userTimezone?.nameFormatted || defaultTimezone;

  const { currentBrand } = useCurrentBrand();

  const classes = useStyles();

  const [taskToEdit, setTaskToEdit] = useState<Task | undefined>(task || undefined);

  const timeControlRef = useRef<TimeControlAndExpensePropsHandles>(null);

  const updateTaskField = <T extends keyof Task>(key: T, value: Task[T]) => {
    setTaskToEdit(
      (prevTask) =>
        ({
          ...prevTask,
          [key]: value,
          whatHasChanged: [...(prevTask?.whatHasChanged || []), key],
        } as Task)
    );
  };

  const [openInfo, setOpenInfo] = useState<boolean>(false);
  const [editEnabled, setEditEnabled] = useState<boolean>(false);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const [slotTime, setSlotTime] = useState<TimeSlotType>(TimeSlotType.NONE);

  const [changeStatusMenu, setChangeStatusMenu] = useState<null | HTMLElement>(null);

  const { fieldHasError, getErrorByField } = useYupValidation(errors);

  const { isLoadingTaskTypeList, isLoadingFleets, isLoadingMembers, isLoadingTeams } =
    isLoadingData;
  const handleExpandTask = useCallback(
    (expandAll: boolean) => {
      setOpenInfo(expandAll);
    },
    [expandAll]
  );

  const { mutate: sendSistrsEmail, isLoading } = useMutation(
    ["send.sistrs.email"],
    (taskId: number) => SendEmailService.sendEmail(taskId),
    {
      onSuccess: () => {
        NotificationToast.success(translate("operationPerformedSuccessfully"));
      },
      onError: (error: AxiosError<Error>) => {
        checkError(error, translate("anErrorOccurredTryAgain"));
      },
    }
  );

  useEffect(() => {
    handleExpandTask(expandAll ?? false);
  }, [expandAll]);

  useEffect(() => {
    if (isSubmitted) handleValidateTask();
  }, [taskToEdit]);

  const taskType = useMemo(() => {
    return tasksTypesList?.find((taskType) => taskType.name === task.type);
  }, [tasksTypesList, task]);

  const assignedToUser = (task: Task, user?: User) => {
    if (!user) return false;
    if (task?.agent && task?.agent?.associatedCompanyId === user?.companyId) return true;
    return false;
  };

  const getIconMilestone = () => {
    if (currentBrand?.primaryColor) return SIGN_POST_3_ICON;

    return SIGN_POST_2_ICON;
  };

  const renderSendToSISTRSbutton = () => {
    if (
      task?.id &&
      task?.status === TaskStatus.FINISHED &&
      process.env.NODE_DATABASE === "production" &&
      isAllied(currentUser?.company)
    ) {
      return (
        <Tooltip title={translate("sendTaskToSistrs")} placement="top">
          <IconButton
            className={classes.defaultIconButton}
            onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
              event.stopPropagation();
              sendSistrsEmail(Number(task?.id));
            }}
          >
            {isLoading ? (
              <CircularProgress className={classes.loadingSISTRS} size={22} />
            ) : (
              <img src={CORNOR_UP_RIGHT_ICON} width="22" height="22" alt="cornor up right icon" />
            )}
          </IconButton>
        </Tooltip>
      );
    }
  };

  const renderTimeControlButton = () => {
    if (
      (taskType && taskType?.role?.toLowerCase() === TaskTypeRole.GENERAL?.toLowerCase()) ||
      !task?.id
    ) {
      return null;
    }

    return (
      <Tooltip title={translate("timeControl")} placement="top">
        <IconButton
          data-cy={`timeControlButton${task?.type}`}
          className={classes.defaultIconButton}
          onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();
            timeControlRef.current?.open(task);
          }}
        >
          <img src={CLOCK_ICON} width="22" height="22" alt="time control icon" />
        </IconButton>
      </Tooltip>
    );
  };

  const renderCancelPublishButton = () => {
    if (task.status === TaskStatus.WAITING_PUBLISH || task.status === TaskStatus.PENDING_ACCEPT) {
      return (
        <Tooltip title={translate("unpublishTask")} placement="top">
          <IconButton
            onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
              event.stopPropagation();
              onUpdate({
                ...task,
                status: TaskStatus.REGISTERED,
                history: `Task ${task.type} has been unpublished`,
                whatHasChanged: [],
              });
            }}
            className={classes.defaultIconButton}
          >
            <IoArrowUndoSharp size={22} color="#475467" />
          </IconButton>
        </Tooltip>
      );
    }

    return null;
  };

  const renderPublishButton = () => {
    const isTaskAgentAndAccepted = !!task?.agentId && !!task?.acceptDate;
    const isAgentCompanyUser = task?.agent?.associatedCompanyId === currentUser?.companyId;
    if (
      task.status === TaskStatus.REGISTERED &&
      (!!task?.leaderId || !!task?.memberLeaderId) &&
      ((isTaskAgentAndAccepted && isAgentCompanyUser) || !task?.agentId)
    ) {
      return (
        <Tooltip title={translate("publishTask")} placement="top">
          <IconButton
            onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
              event.stopPropagation();
              onUpdate({
                ...task,
                status: TaskStatus.WAITING_PUBLISH,
                history: `Task ${task.type} has been published`,
              });
            }}
            className={classes.defaultIconButton}
          >
            <img src={SEND_ICON} width="22" height="22" alt="publish icon" />
          </IconButton>
        </Tooltip>
      );
    }

    return null;
  };

  const renderChangeStatusButton = () => {
    const taskStatusGeneral = [TaskStatus.TO_DO, TaskStatus.STARTED, TaskStatus.FINISHED];

    if (taskType && taskType?.role?.toLowerCase() === TaskTypeRole.GENERAL?.toLowerCase()) {
      return (
        <>
          <Tooltip title={translate("changeStatus")} placement="top">
            <IconButton
              className={classes.defaultIconButton}
              onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                event.stopPropagation();
                setChangeStatusMenu(event.currentTarget);
              }}
            >
              <img src={ARROW_SWAP_ICON} width="22" height="22" alt="change status icon" />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={changeStatusMenu}
            keepMounted={false}
            open={Boolean(changeStatusMenu)}
            onClose={(event: React.MouseEvent<HTMLButtonElement>) => {
              event.stopPropagation();
              setChangeStatusMenu(null);
            }}
            className={classes.menu}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ vertical: "top", horizontal: "left" }}
            PaperProps={{
              className: classes.menuContainer,
            }}
          >
            <TaskStatusMenuTitle>{translate("selectAStatus")}</TaskStatusMenuTitle>
            {taskStatusGeneral.map((taskStatus) => (
              <TaskStatusMenuItem
                key={taskStatus}
                onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                  event.stopPropagation();
                  onUpdate({ ...task, status: taskStatus });
                  setChangeStatusMenu(null);
                }}
                selected={task?.status?.toLowerCase() === taskStatus?.toLowerCase()}
              >
                {taskStatus}
              </TaskStatusMenuItem>
            ))}
          </Menu>
        </>
      );
    }
  };

  const renderEditButton = () => {
    if (task.status === TaskStatus.FINISHED || task.status === TaskStatus.CANCELLED) {
      return null;
    }

    if (
      !(
        currentUser?.company.profileId !== MOVING_AGENT_COMPANY_PROFILE_ID ||
        agentPermissions?.[Number(task?.job?.id)]?.order?.sections?.task?.fields?.general?.edit ===
          true
      )
    ) {
      return null;
    }

    return (
      <Tooltip title={translate("edit")} placement="top">
        <IconButton
          className={`${classes.defaultIconButton} ${
            editEnabled ? classes.selectedIconButton : ""
          }`}
          onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();
            const slotTimeType = (() => {
              switch (task?.period) {
                case TaskStartDatePeriod.VARIABLE_TIME:
                  return TimeSlotType.VARIABLE;
                case TaskStartDatePeriod.FIXED_TIME:
                  return TimeSlotType.FIXED;
                default:
                  return TimeSlotType.NONE;
              }
            })();
            setSlotTime(slotTimeType);
            setEditEnabled((state) => !state);
            setTaskToEdit(task);
            setOpenInfo(true);
          }}
        >
          <img src={EDIT_ICON} width="22" height="22" alt="edit icon" />
        </IconButton>
      </Tooltip>
    );
  };

  const canTaskBeCancelled = (taskStatus: string) => {
    return (
      [
        TaskStatus.REGISTERED,
        TaskStatus.WAITING_PUBLISH,
        TaskStatus.PENDING_ACCEPT,
        TaskStatus.ACCEPTED,
        TaskStatus.TO_DO,
      ].includes(taskStatus as TaskStatus) ||
      (taskStatus === TaskStatus.STARTED &&
        taskType &&
        taskType?.role?.toLowerCase() === TaskTypeRole.GENERAL?.toLowerCase())
    );
  };

  const renderCancelButton = () => {
    if (canTaskBeCancelled(task.status)) {
      return (
        <Tooltip title={translate("cancel")} placement="top">
          <IconButton
            data-cy="buttonDelete"
            className={classes.defaultIconButton}
            onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
              event.stopPropagation();
              onUpdate({
                ...task,
                status: TaskStatus.CANCELLED,
                history: `Task ${task.type} has been cancelled`,
              });
            }}
          >
            <img src={CLOSE_CIRCLE_ICON} width="22" height="22" alt="cancel task icon" />
          </IconButton>
        </Tooltip>
      );
    }

    return null;
  };

  const renderDropdownButton = () => {
    return (
      <IconButton
        onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
          event.stopPropagation();
          setOpenInfo((state) => !state);
        }}
        className={classes.expandIcon}
      >
        {openInfo ? (
          <img src={ARROW_UP_ICON} width="22" height="22" alt="arrow up icon" />
        ) : (
          <img src={ARROW_DOWN_ICON} width="22" height="22" alt="arrow down icon" />
        )}
      </IconButton>
    );
  };

  const renderTimeControlModal = () => {
    if (task.id)
      return (
        // <TimeControlModal
        //   open={timeControlIsOpen}
        //   onClose={() => setTimeControlIsOpen(false)}
        //   task={task}
        // />
        <TimeControlAndExpenseModal ref={timeControlRef} />
      );

    return null;
  };

  const returnDateValue = (date: string, editEnabled?: boolean) => {
    if (!date || !TaskDateUtils.isValidDate(date)) return "";

    if (editEnabled) {
      return TaskDateUtils.extractDateForInput(date, { timezone });
    } else {
      const timeDisplayed = TaskDateUtils.formatForDisplay(date, undefined, { timezone });
      return timeDisplayed;
    }
  };

  const returnMemberOrFleetArray = (type: string) => {
    let newArray: string[] = [];

    switch (type) {
      case TaskAutoSuggestInputType.MEMBER:
        if (task?.members?.length)
          newArray = task.members.map((member) =>
            getMemberDescription(member, { useEmail: false })
          );
        break;
      case TaskAutoSuggestInputType.FLEET:
        if (task?.fleets?.length) {
          newArray = task?.fleets?.map(
            (fleet) =>
              `${getEquipmentName(fleet).fullText} - ${
                convertMetricVolume(Number(fleet.capacityM3), getMeasurement()?.code, 1) ?? ""
              }${getMeasurement()?.code}`
          );
        }
        break;
      default:
        break;
    }

    if (newArray.length) {
      if (newArray.length > 2) {
        const firstTwoItems = newArray.slice(0, 2);
        const lastItems = newArray.slice(2, newArray.length);

        return { value: firstTwoItems, tooltip: lastItems?.join(", ") };
      }

      return { value: newArray };
    }

    return { value: undefined };
  };

  const getDateRangeValue = (value?: number) => {
    switch (value) {
      case TaskStartDatePeriod.MORNING:
        return getStartDateRangeLabel(defaultTimePeriods.morning);
      case TaskStartDatePeriod.AFTERNOON:
        return getStartDateRangeLabel(defaultTimePeriods.afternoon);
      case TaskStartDatePeriod.EVENING:
        return getStartDateRangeLabel(defaultTimePeriods.evening, { considerEvening: true });
      case TaskStartDatePeriod.FULL_DAY:
        return getStartDateRangeLabel(defaultTimePeriods.fullDay, { isFullDay: true });
      case TaskStartDatePeriod.CUSTOM:
        return translate("custom");
      default:
        return "";
    }
  };

  const startTime = useMemo(() => {
    const existingTime = taskToEdit?.startDate
      ? TaskDateUtils.extractTimeForInput(taskToEdit?.startDate, { timezone })
      : undefined;
    return existingTime;
  }, [taskToEdit?.startDate]);

  const endTime = useMemo(
    () =>
      taskToEdit?.endDate
        ? TaskDateUtils.extractTimeForInput(taskToEdit?.endDate, { timezone })
        : "",
    [taskToEdit?.endDate]
  );

  const isMultiDayTask = useMemo(
    () =>
      taskToEdit?.period === TaskStartDatePeriod.MULTI_DAY ||
      taskToEdit?.period === TaskStartDatePeriod.VARIABLE_TIME ||
      taskToEdit?.period === TaskStartDatePeriod.FIXED_TIME,
    [taskToEdit?.period]
  );

  const handleValidateTask = async () => {
    try {
      const schema = yup.object().shape({
        isMultiDayTask: yup.boolean().nullable(),
        task: yup.string().required(translate("thisFieldIsRequired")),
        startDate: yup.string().when("period", {
          is: TaskStartDatePeriod.FIXED_TIME || TaskStartDatePeriod.MULTI_DAY,
          then: yup
            .string()
            .required(translate("thisFieldIsRequired"))
            .typeError(translate("thisFieldIsRequired")),
          otherwise: yup.string().nullable(),
        }),
        endDate: yup.string().when("isMultiDayTask", {
          is: true,
          then: yup.string().when("period", {
            is: TaskStartDatePeriod.FIXED_TIME || TaskStartDatePeriod.MULTI_DAY,
            then: yup
              .string()
              .required(translate("thisFieldIsRequired"))
              .typeError(translate("thisFieldIsRequired")),
            otherwise: yup.string().nullable(),
          }),
          otherwise: yup.string().nullable(),
        }),
        startTime: yup.string().when("period", {
          is: TaskStartDatePeriod.FIXED_TIME,
          then: yup
            .string()
            .required(translate("thisFieldIsRequired"))
            .typeError(translate("thisFieldIsRequired")),
          otherwise: yup.string().nullable(),
        }),
        endTime: yup.string().when("period", {
          is: TaskStartDatePeriod.FIXED_TIME,
          then: yup
            .string()
            .required(translate("thisFieldIsRequired"))
            .test(
              "end-time-greater-than-start-time",
              translate("endTimeMustBeGreaterThanStartTime"),
              function (value) {
                const { startDate, startTime: from } = this.parent;
                if (!from || !value) return true;

                const currentDate = format(new Date(startDate), "yyyy-MM-dd");
                const startTime = new Date(`${currentDate}T${from}`);
                const endTime = new Date(`${currentDate}T${value}`);

                return endTime > startTime;
              }
            )
            .typeError(translate("thisFieldIsRequired")),
          otherwise: yup.string().nullable(),
        }),
        period: yup.number().required(translate("thisFieldIsRequired")),
        timeSlots: yup.array().when("isMultiDayTask", {
          is: true,
          then: yup.array().when("period", {
            is: TaskStartDatePeriod.VARIABLE_TIME,
            then: yup
              .array()
              .min(2, translate("itShouldBeAtLeastTwoDates"))
              .of(
                yup.object().shape({
                  date: yup
                    .string()
                    .required(translate("thisFieldIsRequired"))
                    .typeError(translate("thisFieldIsRequired")),
                  from: yup
                    .string()
                    .required(translate("thisFieldIsRequired"))
                    .typeError(translate("thisFieldIsRequired")),
                  to: yup
                    .string()
                    .required(translate("thisFieldIsRequired"))
                    .test(
                      "end-time-greater-than-start-time",
                      translate("endTimeMustBeGreaterThanStartTime"),
                      function (value) {
                        const { date, from } = this.parent;
                        if (!from || !value) return true;

                        const currentDate = date.split("T")[0];
                        const startTime = new Date(`${currentDate}T${from}`);
                        const endTime = new Date(`${currentDate}T${value}`);

                        return endTime > startTime;
                      }
                    ),
                })
              )
              .typeError(translate("thisFieldIsRequired")),
            otherwise: yup.array().nullable(),
          }),
          otherwise: yup.array().nullable(),
        }),
      });

      const taskToValidate = taskToEdit || task;

      await schema.validate(
        {
          task: taskToValidate.type,
          period: taskToValidate.period,
          timeSlots: taskToValidate.timeSlots,
          startDate: taskToValidate.startDate,
          endDate: taskToValidate.endDate,
          startTime,
          endTime,
          memberLeaderId: taskToValidate?.memberLeaderId,
          isMultiDayTask,
        },
        { abortEarly: false }
      );

      setErrors([]);

      return true;
    } catch (err) {
      if (err instanceof yup.ValidationError) {
        setErrors(err.inner);
        // You can also get a list of just the field names that failed validation
        const invalidFields = err.inner.map((error) => error.path);

        console.error("Invalid fields:", invalidFields);
      }

      return false;
    }
  };

  const handleEditTask = async () => {
    setIsSubmitted(true);

    if (await handleValidateTask()) {
      let startDate = taskToEdit?.startDate || task.startDate;
      let endDate = taskToEdit?.endDate || task.endDate;

      if (slotTime === TimeSlotType.VARIABLE && taskToEdit?.timeSlots?.length) {
        const currentTimeSlots = taskToEdit.timeSlots.sort(
          (a, b) => new Date(a.date!).getTime() - new Date(b.date!).getTime()
        );

        const firstSlot = currentTimeSlots[0];
        const lastSlot = currentTimeSlots[currentTimeSlots.length - 1];

        const startCurrentDate = (firstSlot?.date as string)?.split("T")[0];
        const endCurrentDate = (lastSlot?.date as string)?.split("T")[0];

        startDate = TaskDateUtils.createTaskDateTime(
          { dateInput: startCurrentDate, timeInput: firstSlot?.from },
          timezone
        );
        endDate = TaskDateUtils.createTaskDateTime(
          { dateInput: endCurrentDate, timeInput: lastSlot?.from },
          timezone
        );
      }

      onUpdate({
        ...task,
        ...{
          ...taskToEdit,
          startDate,
          endDate,
          whatHasChanged: taskToEdit?.whatHasChanged
            ? Array.from(new Set(taskToEdit?.whatHasChanged))
            : [],
        },
      });
      setEditEnabled(false);
      setIsSubmitted(false);
    }
  };

  const dateRanged = useMemo(() => {
    const startLabel =
      task.startDate && TaskDateUtils.isValidDate(task.startDate)
        ? TaskDateUtils.formatForDisplay(task.startDate, "hh:mm a", { timezone })
        : "";
    const endLabel =
      task.endDate && TaskDateUtils.isValidDate(task.endDate)
        ? TaskDateUtils.formatForDisplay(task.endDate, "hh:mm a", { timezone })
        : "";
    return `${startLabel} ~ ${endLabel}`;
  }, [task]);

  const defaultTimePeriods = useMemo(() => getStartDatePeriods(new Date()), []);

  const handleChangePeriod = (periodValue: TaskStartDatePeriod) => {
    updateTaskField("period", periodValue);

    const currentDate = taskToEdit?.startDate
      ? TaskDateUtils.extractDateForInput(taskToEdit.startDate, { timezone })
      : TaskDateUtils.getTodayForInput();

    let newStartDate: string;
    let newEndDate: string;

    if (
      [
        TaskStartDatePeriod.MORNING,
        TaskStartDatePeriod.AFTERNOON,
        TaskStartDatePeriod.EVENING,
        TaskStartDatePeriod.FULL_DAY,
      ].includes(periodValue)
    ) {
      // Use predefined periods
      const dates = TaskDateUtils.createPeriodDates(
        currentDate,
        periodValue,
        taskToEdit?.startDate,
        timezone
      );
      newStartDate = dates.startDate;
      newEndDate = dates.endDate;
    } else if (periodValue === TaskStartDatePeriod.MULTI_DAY) {
      // Multi-day: start today, end tomorrow
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      const tomorrowInput = TaskDateUtils.extractDateForInput(tomorrow.toISOString(), { timezone });

      const dates = TaskDateUtils.createMultiDayDates(
        currentDate,
        tomorrowInput,
        undefined,
        undefined,
        true,
        taskToEdit?.startDate,
        taskToEdit?.endDate
      );
      newStartDate = dates.startDate;
      newEndDate = dates.endDate;
    } else {
      // Custom or other periods - preserve existing times or add buffer
      newStartDate =
        taskToEdit?.startDate ||
        TaskDateUtils.createTaskDateTime({ dateInput: currentDate, timeInput: "09:00" }, timezone);
      newEndDate = TaskDateUtils.addBufferTime(newStartDate, periodValue);
    }

    setTaskToEdit(
      (prev) =>
        ({
          ...prev,
          startDate: newStartDate,
          endDate: newEndDate,
          whatHasChanged: [...(prev?.whatHasChanged || []), "startDate", "endDate"],
        } as Task)
    );
  };

  // [TODO]: Change this function to send an array of teams and change the task's root_team_id attribute
  const handleChangeTeam = (teamId: number) => {
    const team = teams?.find((team) => team.id === teamId);

    if (team) {
      const leader = memberLeaders?.find((leader) => leader.id === team?.memberLeader?.userId);
      const teamMembers =
        team?.members?.filter((member) => member.id !== team?.memberLeader?.id) ?? [];

      const teamFleets = team?.fleets || [];
      const memberLeader = team?.memberLeader;

      setTaskToEdit((state) =>
        state
          ? {
              ...state,
              team,
              teamId,
              teams: [team],
              team_id: teamId,
              leader: leader as unknown as User, // Type assertion to match the Task interface
              leaderId: leader?.id,
              members: teamMembers,
              memberLeader,
              memberLeaderId: memberLeader?.id,
              fleets: teamFleets,
              whatHasChanged: [...(state.whatHasChanged || []), "team"],
            }
          : ({
              team,
              teamId,
              teams: [team],
              team_id: teamId,
              leader: leader as unknown as User, // Type assertion to match the Task interface
              leaderId: leader?.id,
              members: teamMembers,
              memberLeader,
              memberLeaderId: memberLeader?.id,
              fleets: teamFleets,
              whatHasChanged: ["team"],
            } as Task)
      );
    } else {
      setTaskToEdit((state) =>
        state
          ? {
              ...state,
              team: undefined,
              teamId,
              teams: [],
              team_id: teamId,
              whatHasChanged: [...(state.whatHasChanged || []), "team"],
            }
          : ({
              team: undefined,
              teamId,
              team_id: teamId,
              teams: [],
              whatHasChanged: ["team"],
            } as unknown as Task)
      );
    }
  };

  const renderMultidayRadio = () => {
    return (
      <RadioButton
        label={translate("isThisAMultiDayTask")}
        onChange={(event) => {
          setSlotTime(TimeSlotType.NONE);
          setTaskToEdit((prev) => ({ ...(prev || {}), timeSlots: [] } as Task));
          const rawValue = event.target.value;

          const parsedValue = rawValue.includes(",")
            ? rawValue.split(",").map((value) => Number(value.trim()))
            : Number(rawValue);

          handleChangePeriod(
            (Array.isArray(parsedValue) ? parsedValue.at(0) : parsedValue) ??
              TaskStartDatePeriod.MORNING
          );
        }}
        options={[
          {
            label: translate("yes"),
            value: [
              TaskStartDatePeriod.MULTI_DAY,
              TaskStartDatePeriod.VARIABLE_TIME,
              TaskStartDatePeriod.FIXED_TIME,
            ],
          },
          {
            label: translate("no"),
            value: [
              TaskStartDatePeriod.MORNING,
              TaskStartDatePeriod.AFTERNOON,
              TaskStartDatePeriod.EVENING,
              TaskStartDatePeriod.FULL_DAY,
              TaskStartDatePeriod.CUSTOM,
            ],
          },
        ]}
        value={taskToEdit?.period || task.period}
      />
    );
  };

  useEffect(() => {
    if (task) setTaskToEdit(task);
    if (task.period === TaskStartDatePeriod.FIXED_TIME) setSlotTime(TimeSlotType.FIXED);
    if (task.period === TaskStartDatePeriod.VARIABLE_TIME) setSlotTime(TimeSlotType.VARIABLE);
  }, [task]);

  useEffect(() => {
    if (!taskToEdit?.endDate || !taskToEdit?.startDate) return;

    let taskEndDate = new Date(taskToEdit?.endDate);
    const taskStartDate = new Date(taskToEdit?.startDate);

    if (
      isBefore(taskEndDate, taskStartDate) ||
      ([
        TaskStartDatePeriod.MULTI_DAY,
        TaskStartDatePeriod.VARIABLE_TIME,
        TaskStartDatePeriod.FIXED_TIME,
      ].includes(taskToEdit?.period!) &&
        isSameDay(taskEndDate, taskStartDate))
    ) {
      if (!taskToEdit?.period) return;

      taskEndDate = new Date(taskStartDate);
      taskEndDate = add(taskEndDate, PERIOD_BUFFER_TIME[taskToEdit?.period as TaskStartDatePeriod]);

      updateTaskField("endDate", taskEndDate.toISOString());
    }
  }, [taskToEdit?.period, taskToEdit?.startDate, taskToEdit?.endDate]);

  const returnTeamName = useMemo(() => {
    if (task.teams?.length) {
      if (task.teams?.length === 1) return task.teams[0]?.name;

      const team = task.teams.find((team) => {
        const diffInDays = differenceInDays(
          startOfDay(new Date(team.createdTo || "")),
          startOfDay(new Date(task.startDate))
        );
        if (diffInDays === 0) return team;
        return false;
      });

      return team?.name;
    }

    if (task?.team) {
      return task.team.name;
    }

    return "";
  }, [task]);

  const returnTeam = useMemo(() => {
    if (taskToEdit?.teams?.length) {
      if (taskToEdit.teams?.length === 1) return taskToEdit.teams[0];

      const team = taskToEdit.teams.find((team) => {
        const diffInDays = differenceInDays(
          startOfDay(new Date(team.createdTo || "")),
          startOfDay(new Date(taskToEdit.startDate))
        );
        if (diffInDays === 0) return team;
        return false;
      });

      return team;
    }

    if (task?.team) {
      return task.team;
    }

    return null;
  }, [taskToEdit]);

  const disableOnNonEditableStatus = useMemo(() => {
    const editableStatus = [TaskStatus.REGISTERED, TaskStatus.TO_DO];

    if (!taskToEdit?.status) {
      return true;
    }

    return !editableStatus.includes(taskToEdit.status as TaskStatus) || isOnViewMode;
  }, [taskToEdit, isOnViewMode]);

  return (
    <section>
      {isOnViewMode ? (
        <TaskViewMode
          task={task}
          expand={expandAll}
          taskTypeSequence={taskTypeSequence}
          sendSistrsEmail={sendSistrsEmail}
          isLoading={isLoading}
        />
      ) : (
        <>
          <TaskItemContainer isOpen={openInfo} onClick={() => setOpenInfo(true)}>
            <Grid container direction="column">
              <Grid container>
                <TaskItemHeader>
                  <TitleAndStatusContainer>
                    <TaskTitle>
                      {task?.type} {taskTypeSequence && `(${taskTypeSequence})`}
                    </TaskTitle>
                    <div data-cy="taskStatus">
                      <BadgeJobStatus
                        key={task.status}
                        className={classes.badge}
                        value={task?.status}
                      />
                    </div>
                    {task?.agentId && (
                      <div data-cy="taskStatus">
                        <BadgeJobStatus
                          key={task.agentId}
                          className={classes.badge}
                          value={translate("agent")}
                        />
                      </div>
                    )}
                    {isMultiDayTask && (
                      <div data-cy="taskIsMultiDay">
                        <MultiDayTaskBadge key={task.status}>
                          {translate("multi_day")}
                        </MultiDayTaskBadge>
                      </div>
                    )}
                  </TitleAndStatusContainer>
                  <HeaderActions>
                    {renderTimeControlButton()}

                    {renderSendToSISTRSbutton()}

                    {renderCancelPublishButton()}
                    {renderPublishButton()}
                    {renderChangeStatusButton()}
                    {renderEditButton()}
                    {renderCancelButton()}
                    {renderDropdownButton()}
                  </HeaderActions>
                </TaskItemHeader>
              </Grid>
              {openInfo && (
                <OpenInfoSection>
                  <>
                    <Grid container direction="row" alignItems="flex-start" spacing={2}>
                      <Grid item container xs={12} spacing={2}>
                        {editEnabled ? (
                          <React.Fragment>
                            <Grid item xs={isMultiDayTask ? 2 : 12}>
                              {renderMultidayRadio()}
                            </Grid>
                            {isMultiDayTask && (
                              <Grid item xs={10}>
                                <RadioButton
                                  label={translate("isTimeSlotRequiredForMultiDayTask")}
                                  onChange={(event) => {
                                    if (event.target.value === TimeSlotType.VARIABLE) {
                                      setTaskToEdit(
                                        (prev) =>
                                          ({
                                            ...(prev || {}),
                                            period: TaskStartDatePeriod.VARIABLE_TIME,
                                            timeSlots: [
                                              { date: new Date().toISOString() } as TimeSlotTask,
                                            ],
                                          } as Task)
                                      );
                                    } else {
                                      const taskPeriod =
                                        event.target.value === TimeSlotType.FIXED
                                          ? TaskStartDatePeriod.FIXED_TIME
                                          : TaskStartDatePeriod.MULTI_DAY;
                                      setTaskToEdit(
                                        (prev) =>
                                          ({
                                            ...(prev || {}),
                                            period: taskPeriod,
                                            timeSlots: [],
                                          } as Task)
                                      );
                                    }

                                    setSlotTime(event.target.value as TimeSlotType);
                                  }}
                                  value={slotTime}
                                  options={[
                                    { label: translate("no"), value: TimeSlotType.NONE },
                                    {
                                      label: translate("fixedTimeSlot"),
                                      value: TimeSlotType.FIXED,
                                    },
                                    {
                                      label: translate("variableTimeSlot"),
                                      value: TimeSlotType.VARIABLE,
                                    },
                                  ]}
                                />
                              </Grid>
                            )}
                            {isMultiDayTask && slotTime === TimeSlotType.VARIABLE && (
                              <VariableSlotTime
                                task={taskToEdit}
                                setTask={(item) =>
                                  setTaskToEdit(
                                    (prev) => ({ ...(prev || {}), ...(item || {}) } as Task)
                                  )
                                }
                                currentTimezone={timezone}
                              />
                            )}
                            {slotTime !== TimeSlotType.VARIABLE && (
                              <>
                                <Grid item xs={3}>
                                  <SkeletonComponent isLoading={isLoadingTaskTypeList} isInput>
                                    <Input
                                      required
                                      disabled={disableOnNonEditableStatus}
                                      type="date"
                                      name="date"
                                      label={translate(isMultiDayTask ? "startDate" : "date")}
                                      value={
                                        taskToEdit?.startDate
                                          ? returnDateValue(taskToEdit.startDate, editEnabled)
                                          : ""
                                      }
                                      error={fieldHasError("date")}
                                      errorMessage={getErrorByField("date")}
                                      onChange={(event) => {
                                        if (event?.target?.value && taskToEdit) {
                                          const newDateInput = event.target.value;
                                          const existingTime = taskToEdit.startDate
                                            ? TaskDateUtils.extractTimeForInput(
                                                taskToEdit.startDate
                                              )
                                            : undefined;

                                          const startDate = TaskDateUtils.createTaskDateTime(
                                            {
                                              dateInput: newDateInput,
                                              timeInput: existingTime,
                                              preserveExistingTime: true,
                                              existingDateString: taskToEdit.startDate,
                                            },
                                            timezone
                                          );

                                          if (startDate) {
                                            setTaskToEdit((state) =>
                                              state
                                                ? {
                                                    ...state,
                                                    startDate,
                                                    whatHasChanged: [
                                                      ...(state.whatHasChanged || []),
                                                      "startDate",
                                                    ],
                                                  }
                                                : ({
                                                    startDate,
                                                    whatHasChanged: ["startDate"],
                                                  } as Task)
                                            );
                                          }
                                        }
                                      }}
                                      min={TaskDateUtils.getMinimumDate(19)}
                                    />
                                  </SkeletonComponent>
                                </Grid>
                                {task?.agent && (
                                  <>
                                    <Grid item xs={3}>
                                      <SkeletonComponent isLoading={isLoadingTaskTypeList} isInput>
                                        <Input
                                          required
                                          disabled={disableOnNonEditableStatus}
                                          type="date"
                                          name="date"
                                          label={translate("accepted")}
                                          value={
                                            taskToEdit?.acceptDate
                                              ? returnDateValue(taskToEdit.acceptDate, editEnabled)
                                              : ""
                                          }
                                          error={fieldHasError("date")}
                                          errorMessage={getErrorByField("date")}
                                          onChange={(event) => {
                                            if (event?.target?.value && taskToEdit) {
                                              const newDateInput = event.target.value;
                                              const existingTime = taskToEdit.startDate
                                                ? TaskDateUtils.extractTimeForInput(
                                                    taskToEdit.startDate
                                                  )
                                                : undefined;

                                              const startDate = TaskDateUtils.createTaskDateTime(
                                                {
                                                  dateInput: newDateInput,
                                                  timeInput: existingTime,
                                                  preserveExistingTime: true,
                                                  existingDateString: taskToEdit.startDate,
                                                },
                                                timezone
                                              );

                                              if (startDate) {
                                                setTaskToEdit((state) =>
                                                  state
                                                    ? {
                                                        ...state,
                                                        startDate,
                                                        whatHasChanged: [
                                                          ...(state.whatHasChanged || []),
                                                          "startDate",
                                                        ],
                                                      }
                                                    : ({
                                                        startDate,
                                                        whatHasChanged: ["startDate"],
                                                      } as Task)
                                                );
                                              }
                                            }
                                          }}
                                          min={TaskDateUtils.getMinimumDate(19)}
                                        />
                                      </SkeletonComponent>
                                    </Grid>
                                    <Grid item xs={3}>
                                      <SkeletonComponent isLoading={isLoadingTaskTypeList} isInput>
                                        <Input
                                          required
                                          disabled={disableOnNonEditableStatus}
                                          type="date"
                                          name="date"
                                          label={translate("started")}
                                          value={
                                            taskToEdit?.startDate
                                              ? returnDateValue(taskToEdit.startDate, editEnabled)
                                              : ""
                                          }
                                          error={fieldHasError("date")}
                                          errorMessage={getErrorByField("date")}
                                          onChange={(event) => {
                                            if (event?.target?.value && taskToEdit) {
                                              const newDateInput = event.target.value;
                                              const existingTime = taskToEdit.startDate
                                                ? TaskDateUtils.extractTimeForInput(
                                                    taskToEdit.startDate
                                                  )
                                                : undefined;

                                              const startDate = TaskDateUtils.createTaskDateTime(
                                                {
                                                  dateInput: newDateInput,
                                                  timeInput: existingTime,
                                                  preserveExistingTime: true,
                                                  existingDateString: taskToEdit.startDate,
                                                },
                                                timezone
                                              );

                                              if (startDate) {
                                                setTaskToEdit((state) =>
                                                  state
                                                    ? {
                                                        ...state,
                                                        startDate,
                                                        whatHasChanged: [
                                                          ...(state.whatHasChanged || []),
                                                          "startDate",
                                                        ],
                                                      }
                                                    : ({
                                                        startDate,
                                                        whatHasChanged: ["startDate"],
                                                      } as Task)
                                                );
                                              }
                                            }
                                          }}
                                          min={TaskDateUtils.getMinimumDate(19)}
                                        />
                                      </SkeletonComponent>
                                    </Grid>
                                    <Grid item xs={3}>
                                      <SkeletonComponent isLoading={isLoadingTaskTypeList} isInput>
                                        <Input
                                          required
                                          disabled={disableOnNonEditableStatus}
                                          type="date"
                                          name="date"
                                          label={translate("completed")}
                                          value={
                                            taskToEdit?.finishDate
                                              ? returnDateValue(taskToEdit.finishDate, editEnabled)
                                              : ""
                                          }
                                          error={fieldHasError("date")}
                                          errorMessage={getErrorByField("date")}
                                          onChange={(event) => {
                                            if (event?.target?.value && taskToEdit) {
                                              const newDateInput = event.target.value;
                                              const existingTime = taskToEdit.startDate
                                                ? TaskDateUtils.extractTimeForInput(
                                                    taskToEdit.startDate
                                                  )
                                                : undefined;

                                              const startDate = TaskDateUtils.createTaskDateTime(
                                                {
                                                  dateInput: newDateInput,
                                                  timeInput: existingTime,
                                                  preserveExistingTime: true,
                                                  existingDateString: taskToEdit.startDate,
                                                },
                                                timezone
                                              );

                                              if (startDate) {
                                                setTaskToEdit((state) =>
                                                  state
                                                    ? {
                                                        ...state,
                                                        startDate,
                                                        whatHasChanged: [
                                                          ...(state.whatHasChanged || []),
                                                          "startDate",
                                                        ],
                                                      }
                                                    : ({
                                                        startDate,
                                                        whatHasChanged: ["startDate"],
                                                      } as Task)
                                                );
                                              }
                                            }
                                          }}
                                          min={TaskDateUtils.getMinimumDate(19)}
                                        />
                                      </SkeletonComponent>
                                    </Grid>
                                  </>
                                )}
                                {!!isMultiDayTask && (
                                  <Grid item xs={3}>
                                    <SkeletonComponent isLoading={isLoadingTaskTypeList} isInput>
                                      <Input
                                        required
                                        disabled={disableOnNonEditableStatus}
                                        type="date"
                                        name="endDate"
                                        label={translate("endDate")}
                                        value={
                                          taskToEdit?.endDate
                                            ? returnDateValue(taskToEdit.endDate, editEnabled)
                                            : ""
                                        }
                                        error={fieldHasError("endDate")}
                                        errorMessage={getErrorByField("endDate")}
                                        onChange={(event) => {
                                          if (event?.target?.value && taskToEdit) {
                                            const newDateInput = event.target.value;
                                            const existingTime = taskToEdit.endDate
                                              ? TaskDateUtils.extractTimeForInput(
                                                  taskToEdit.endDate
                                                )
                                              : undefined;

                                            const endDate = TaskDateUtils.createTaskDateTime(
                                              {
                                                dateInput: newDateInput,
                                                timeInput: existingTime,
                                                preserveExistingTime: true,
                                                existingDateString: taskToEdit.endDate,
                                              },
                                              timezone
                                            );

                                            if (endDate) {
                                              setTaskToEdit((state) =>
                                                state
                                                  ? {
                                                      ...state,
                                                      endDate,
                                                      whatHasChanged: [
                                                        ...(state.whatHasChanged || []),
                                                        "endDate",
                                                      ],
                                                    }
                                                  : ({
                                                      endDate,
                                                      whatHasChanged: ["endDate"],
                                                    } as Task)
                                              );
                                            }
                                          }
                                        }}
                                        min={sub(new Date(), { days: 19 })}
                                      />
                                    </SkeletonComponent>
                                  </Grid>
                                )}
                                {isMultiDayTask && slotTime === TimeSlotType.FIXED && (
                                  <>
                                    <Grid item xs={3}>
                                      <Input
                                        required
                                        type="time"
                                        name="fromTime"
                                        label={translate("from")}
                                        value={startTime}
                                        onChange={(event) => {
                                          if (event?.target?.value && taskToEdit) {
                                            const newTimeInput = event.target.value;
                                            const existingDate = taskToEdit.startDate
                                              ? TaskDateUtils.extractDateForInput(
                                                  taskToEdit.startDate
                                                )
                                              : TaskDateUtils.getTodayForInput();

                                            const startDate = TaskDateUtils.createTaskDateTime(
                                              {
                                                dateInput: existingDate,
                                                timeInput: newTimeInput,
                                              },
                                              timezone
                                            );

                                            if (startDate) {
                                              setTaskToEdit((state) =>
                                                state
                                                  ? {
                                                      ...state,
                                                      startDate,
                                                      whatHasChanged: [
                                                        ...(state.whatHasChanged || []),
                                                        "startDate",
                                                      ],
                                                    }
                                                  : ({
                                                      startDate,
                                                      whatHasChanged: ["startDate"],
                                                    } as Task)
                                              );
                                            }
                                          }
                                        }}
                                      />
                                    </Grid>
                                    <Grid item xs={3}>
                                      <Input
                                        required
                                        type="time"
                                        name="toTime"
                                        label={translate("to")}
                                        value={endTime}
                                        onChange={(event) => {
                                          if (event?.target?.value && taskToEdit) {
                                            const newTimeInput = event.target.value;
                                            const existingDate = taskToEdit.endDate
                                              ? TaskDateUtils.extractDateForInput(
                                                  taskToEdit.endDate
                                                )
                                              : TaskDateUtils.getTodayForInput();

                                            const endDate = TaskDateUtils.createTaskDateTime(
                                              {
                                                dateInput: existingDate,
                                                timeInput: newTimeInput,
                                              },
                                              timezone
                                            );

                                            if (endDate) {
                                              setTaskToEdit((state) =>
                                                state
                                                  ? {
                                                      ...state,
                                                      endDate,
                                                      whatHasChanged: [
                                                        ...(state.whatHasChanged || []),
                                                        "endDate",
                                                      ],
                                                    }
                                                  : ({
                                                      endDate,
                                                      whatHasChanged: ["endDate"],
                                                    } as Task)
                                              );
                                            }
                                          }
                                        }}
                                        error={!!getErrorByField("endTime")}
                                        errorMessage={getErrorByField("endTime")}
                                      />
                                    </Grid>
                                  </>
                                )}
                              </>
                            )}
                            {!isMultiDayTask && !task?.agent && (
                              <React.Fragment>
                                <Grid item xs={3}>
                                  <Select
                                    label={translate("period")}
                                    placeholder={translate("select")}
                                    required
                                    value={taskToEdit?.period || ""}
                                    onChange={(event) =>
                                      handleChangePeriod(Number(event.target.value))
                                    }
                                    renderValue={() => getDateRangeValue(taskToEdit?.period)}
                                  >
                                    <PeriodMenuItem value={TaskStartDatePeriod.MORNING}>
                                      <MenuItemTitle>{translate("morning")}</MenuItemTitle>
                                      {getStartDateRangeLabel(defaultTimePeriods.morning)}
                                    </PeriodMenuItem>
                                    <PeriodMenuItem value={TaskStartDatePeriod.AFTERNOON}>
                                      <MenuItemTitle>{translate("afternoon")}</MenuItemTitle>
                                      {getStartDateRangeLabel(defaultTimePeriods.afternoon)}
                                    </PeriodMenuItem>
                                    <PeriodMenuItem value={TaskStartDatePeriod.EVENING}>
                                      <MenuItemTitle>{translate("evening")}</MenuItemTitle>
                                      {getStartDateRangeLabel(defaultTimePeriods.evening, {
                                        considerEvening: true,
                                      })}
                                    </PeriodMenuItem>
                                    <PeriodMenuItem value={TaskStartDatePeriod.FULL_DAY}>
                                      <MenuItemTitle>{translate("fullDay")}</MenuItemTitle>
                                      {getStartDateRangeLabel(defaultTimePeriods.fullDay, {
                                        isFullDay: true,
                                      })}
                                    </PeriodMenuItem>
                                    <MenuItem value={TaskStartDatePeriod.CUSTOM}>
                                      {translate("customTime")}
                                    </MenuItem>
                                  </Select>
                                </Grid>
                                <Grid item xs={3}>
                                  <Input
                                    required
                                    disabled={taskToEdit?.period !== TaskStartDatePeriod.CUSTOM}
                                    type="time"
                                    name="fromTime"
                                    label={translate("from")}
                                    value={startTime}
                                    onChange={(event) => {
                                      if (event?.target?.value && taskToEdit) {
                                        const timeSplitted = event.target.value.split(":");

                                        if (timeSplitted && timeSplitted?.length > 0) {
                                          const oldDate = new Date(taskToEdit?.startDate);

                                          oldDate.setHours(
                                            Number(timeSplitted[0]),
                                            Number(timeSplitted[1])
                                          );

                                          const startDate = zonedTimeToUtc(
                                            oldDate,
                                            Intl.DateTimeFormat().resolvedOptions().timeZone
                                          ).toISOString();

                                          if (startDate)
                                            setTaskToEdit((state) =>
                                              state
                                                ? {
                                                    ...state,
                                                    startDate,
                                                    whatHasChanged: [
                                                      ...(state.whatHasChanged || []),
                                                      "startDate",
                                                    ],
                                                  }
                                                : ({
                                                    startDate,
                                                    whatHasChanged: ["startDate"],
                                                  } as Task)
                                            );
                                        }
                                      }
                                    }}
                                  />
                                </Grid>
                                <Grid item xs={3}>
                                  <Input
                                    required
                                    disabled={taskToEdit?.period !== TaskStartDatePeriod.CUSTOM}
                                    type="time"
                                    name="toTime"
                                    label={translate("to")}
                                    value={endTime}
                                    onChange={(event) => {
                                      if (event?.target?.value && taskToEdit) {
                                        const timeSplitted = event.target.value.split(":");

                                        if (
                                          timeSplitted &&
                                          timeSplitted?.length > 0 &&
                                          taskToEdit?.endDate
                                        ) {
                                          const oldDate = new Date(taskToEdit?.endDate);

                                          oldDate.setHours(
                                            Number(timeSplitted[0]),
                                            Number(timeSplitted[1])
                                          );

                                          const endDate = zonedTimeToUtc(
                                            oldDate,
                                            Intl.DateTimeFormat().resolvedOptions().timeZone
                                          ).toISOString();

                                          if (endDate)
                                            setTaskToEdit((state) =>
                                              state
                                                ? {
                                                    ...state,
                                                    endDate,
                                                    whatHasChanged: [
                                                      ...(state.whatHasChanged || []),
                                                      "endDate",
                                                    ],
                                                  }
                                                : ({ endDate, whatHasChanged: ["endDate"] } as Task)
                                            );
                                        }
                                      }
                                    }}
                                    error={!!getErrorByField("endTime")}
                                    errorMessage={getErrorByField("endTime")}
                                  />
                                </Grid>
                              </React.Fragment>
                            )}
                          </React.Fragment>
                        ) : (
                          <React.Fragment>
                            <Grid item sm={12} md={3}>
                              <FieldView
                                label={translate(task?.startDate ? "startDate" : "assigned")}
                                labelClass={classes.editDisabledLabel}
                                value={
                                  task?.startDate
                                    ? returnDateValue(task.startDate, editEnabled)
                                    : ""
                                }
                              />
                            </Grid>
                            {task?.agent && (
                              <>
                                <Grid item sm={12} md={3}>
                                  <FieldView
                                    label={translate("accepted")}
                                    labelClass={classes.editDisabledLabel}
                                    value={task?.acceptDate}
                                  />
                                </Grid>
                                <Grid item sm={12} md={3}>
                                  <FieldView
                                    label={translate("started")}
                                    labelClass={classes.editDisabledLabel}
                                    value={task?.startDate}
                                  />
                                </Grid>
                                <Grid item sm={12} md={3}>
                                  <FieldView
                                    label={translate("completed")}
                                    labelClass={classes.editDisabledLabel}
                                    value={task?.endDate}
                                  />
                                </Grid>
                              </>
                            )}

                            {isMultiDayTask ? (
                              <Grid item sm={12} md={3}>
                                <FieldView
                                  label={translate("endDate")}
                                  labelClass={classes.editDisabledLabel}
                                  value={
                                    task?.endDate ? returnDateValue(task.endDate, editEnabled) : ""
                                  }
                                />
                              </Grid>
                            ) : (
                              <Grid item sm={12} md={3} lg={3}>
                                <FieldView
                                  label={translate("time")}
                                  labelClass={classes.editDisabledLabel}
                                  value={dateRanged}
                                />
                              </Grid>
                            )}
                          </React.Fragment>
                        )}
                      </Grid>
                      <Grid item container xs={12} spacing={2}>
                        {editEnabled ? (
                          <React.Fragment>
                            {(!task?.agent || assignedToUser(task, currentUser)) && (
                              <>
                                <Grid item sm={12} md={3}>
                                  <SkeletonComponent isLoading={isLoadingTeams}>
                                    <Select
                                      disabled={disableOnNonEditableStatus}
                                      label={translate("team")}
                                      placeholder={translate("select")}
                                      value={
                                        returnTeam?.isCrew ? returnTeam?.rootTeamId : returnTeam?.id
                                      }
                                      onChange={(event: SelectChangeEvent<any>) =>
                                        handleChangeTeam(event.target.value)
                                      }
                                      renderValue={() => returnTeam?.name || ""}
                                    >
                                      {teams?.map((team) => (
                                        <MenuItem key={team.id} value={team.id}>
                                          {team.name}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </SkeletonComponent>
                                </Grid>
                                <Grid item sm={12} md={3}>
                                  <AutoSuggest
                                    disabled={disableOnNonEditableStatus}
                                    label={translate("leader")}
                                    placeholder={translate("select")}
                                    error={fieldHasError("memberLeaderId")}
                                    errorMessage={getErrorByField("memberLeaderId")}
                                    inputValue={getMemberDescription(taskToEdit?.memberLeader)}
                                    getOptionLabel={getMemberDescription}
                                    getOptionSelected={(option: Member, value: Member) => {
                                      return option.id === value.id;
                                    }}
                                    value={taskToEdit?.memberLeader}
                                    options={memberLeaders}
                                    onChange={(memberLeader: Member) => {
                                      setTaskToEdit((state) =>
                                        state
                                          ? ({
                                              ...state,
                                              leaderId: memberLeader?.userId || null,
                                              leader: memberLeader?.user || null,
                                              memberLeaderId: memberLeader?.id || null,
                                              memberLeader: memberLeader || null,
                                              whatHasChanged: [
                                                ...(state.whatHasChanged || []),
                                                "memberLeader",
                                              ],
                                            } as Task)
                                          : ({
                                              memberLeader,
                                              memberLeaderId: memberLeader?.id,
                                              leaderId: memberLeader?.userId,
                                              leader: memberLeader?.user,
                                              whatHasChanged: ["memberLeader"],
                                            } as Task)
                                      );
                                    }}
                                  />
                                </Grid>
                                <Grid item sm={12} md={3}>
                                  <SkeletonComponent isLoading={isLoadingMembers}>
                                    <AutoSuggest
                                      disabled={disableOnNonEditableStatus}
                                      data-cy="autosuggestUserWithBadge"
                                      label={translate("members")}
                                      placeholder={
                                        taskToEdit?.members?.length ? "" : translate("select")
                                      }
                                      getOptionLabel={getMemberDescription}
                                      getOptionSelected={(option: Member, value: Member) =>
                                        option?.id === value?.id
                                      }
                                      value={taskToEdit?.members || []}
                                      multiple
                                      onChange={(members: Member[]) =>
                                        setTaskToEdit((state) =>
                                          state
                                            ? {
                                                ...state,
                                                members,
                                                whatHasChanged: [
                                                  ...(state.whatHasChanged || []),
                                                  "members",
                                                ],
                                              }
                                            : ({ members, whatHasChanged: ["members"] } as Task)
                                        )
                                      }
                                      options={members}
                                    />
                                  </SkeletonComponent>
                                </Grid>
                                <Grid item sm={12} md={3}>
                                  <SkeletonComponent isLoading={isLoadingFleets}>
                                    <AutoSuggest
                                      disabled={disableOnNonEditableStatus}
                                      data-cy="autosuggestFleetWithBadge"
                                      label={translate("equipment")}
                                      placeholder={
                                        taskToEdit?.fleets?.length ? "" : translate("select")
                                      }
                                      getOptionLabel={(fleet?: Fleet) => {
                                        if (fleet) {
                                          const { fullText } = getEquipmentName(
                                            fleet,
                                            translate("general"),
                                            translate("fleet")
                                          );

                                          return fullText;
                                        }

                                        return "";
                                      }}
                                      getOptionSelected={(option: Fleet, value: Fleet) =>
                                        option?.id === value?.id
                                      }
                                      value={taskToEdit?.fleets || []}
                                      multiple
                                      onChange={(fleets: Fleet[]) =>
                                        setTaskToEdit((state) =>
                                          state
                                            ? {
                                                ...state,
                                                fleets,
                                                whatHasChanged: [
                                                  ...(state.whatHasChanged || []),
                                                  "fleets",
                                                ],
                                              }
                                            : ({ fleets, whatHasChanged: ["fleets"] } as Task)
                                        )
                                      }
                                      options={fleets}
                                    />
                                  </SkeletonComponent>
                                </Grid>
                              </>
                            )}

                            {task?.agent && (
                              <>
                                <Grid item sm={12} md={3}>
                                  <FieldView
                                    multipleLines
                                    label={translate("agentName")}
                                    labelClass={classes.editDisabledLabel}
                                    value={task?.agent?.entity}
                                  />
                                </Grid>
                                <Grid item sm={12} md={3}>
                                  <FieldView
                                    multipleLines
                                    label={translate("mainContact")}
                                    labelClass={classes.editDisabledLabel}
                                    value={task?.agent?.name}
                                  />
                                </Grid>
                                <Grid item sm={12} md={3}>
                                  <FieldView
                                    multipleLines
                                    label={translate("phone")}
                                    labelClass={classes.editDisabledLabel}
                                    value={task?.agent?.phone}
                                  />
                                </Grid>
                                <Grid item sm={12} md={3}>
                                  <FieldView
                                    multipleLines
                                    label={translate("email")}
                                    labelClass={classes.editDisabledLabel}
                                    value={task?.agent?.email}
                                  />
                                </Grid>
                                <Grid item sm={12}>
                                  <FieldView
                                    multipleLines
                                    label={translate("additionalInstructions")}
                                    labelClass={classes.editDisabledLabel}
                                    value={task?.additionalInstructions}
                                  />
                                </Grid>
                              </>
                            )}
                          </React.Fragment>
                        ) : (
                          <React.Fragment>
                            {(!task?.agent || assignedToUser(task, currentUser)) && (
                              <>
                                <Grid item sm={12} md={3}>
                                  <FieldView
                                    label={translate("team")}
                                    labelClass={classes.editDisabledLabel}
                                    value={returnTeamName}
                                  />
                                </Grid>
                                <Grid item sm={12} md={3}>
                                  <FieldView
                                    label={translate("leader")}
                                    labelClass={classes.editDisabledLabel}
                                    value={getMemberDescription(task?.memberLeader)}
                                  />
                                </Grid>
                                <Grid item sm={12} md={3}>
                                  <FieldView
                                    multipleLines
                                    label={translate("members")}
                                    labelClass={classes.editDisabledLabel}
                                    value={
                                      returnMemberOrFleetArray(TaskAutoSuggestInputType.MEMBER)
                                        ?.value
                                    }
                                    tooltip={
                                      returnMemberOrFleetArray(TaskAutoSuggestInputType.MEMBER)
                                        ?.tooltip
                                    }
                                  />
                                </Grid>
                                <Grid item sm={12} md={3}>
                                  <FieldView
                                    multipleLines
                                    label={translate("equipment")}
                                    labelClass={classes.editDisabledLabel}
                                    value={
                                      returnMemberOrFleetArray(TaskAutoSuggestInputType.FLEET)
                                        ?.value
                                    }
                                    tooltip={
                                      returnMemberOrFleetArray(TaskAutoSuggestInputType.FLEET)
                                        ?.tooltip
                                    }
                                  />
                                </Grid>
                              </>
                            )}
                            {task?.agent && (
                              <>
                                <Grid item sm={12} md={3}>
                                  <div className={classes.iconMainContact}>
                                    <FieldView
                                      multipleLines
                                      label={translate("agentName")}
                                      labelClass={classes.editDisabledLabel}
                                      value={task?.agent?.entity}
                                      icon={
                                        task?.agent?.isShipeeziNetwork ? (
                                          <Share
                                            size={18}
                                            color={colors.gray600}
                                            className={classes.icon}
                                          />
                                        ) : undefined
                                      }
                                    />
                                  </div>
                                </Grid>
                                <Grid item sm={12} md={3}>
                                  <FieldView
                                    multipleLines
                                    label={translate("mainContact")}
                                    labelClass={classes.editDisabledLabel}
                                    value={task?.agent?.name}
                                  />
                                </Grid>
                                <Grid item sm={12} md={3}>
                                  <FieldView
                                    multipleLines
                                    label={translate("phone")}
                                    labelClass={classes.editDisabledLabel}
                                    value={task?.agent?.phone}
                                  />
                                </Grid>
                                <Grid item sm={12} md={3}>
                                  <FieldView
                                    multipleLines
                                    label={translate("email")}
                                    labelClass={classes.editDisabledLabel}
                                    value={task?.agent?.email}
                                  />
                                </Grid>
                                <Grid item sm={12}>
                                  <FieldView
                                    multipleLines
                                    label={translate("additionalInstructions")}
                                    labelClass={classes.editDisabledLabel}
                                    value={task?.additionalInstructions}
                                  />
                                </Grid>
                              </>
                            )}
                          </React.Fragment>
                        )}
                      </Grid>
                    </Grid>

                    <GeneralWrapper>
                      <GeneralCommentsComponent
                        enableEmptyState={editEnabled}
                        isViewMode={!editEnabled}
                        generalCommentsTitle={translate("comments")}
                        comments={taskToEdit?.comments}
                        onAddComment={(comment) =>
                          setTaskToEdit((state) =>
                            state
                              ? {
                                  ...state,
                                  comments: [
                                    ...(taskToEdit?.comments || []),
                                    comment as TaskComment,
                                  ],
                                  whatHasChanged: [...(state.whatHasChanged || []), "comments"],
                                }
                              : ({
                                  comments: [
                                    ...(taskToEdit?.comments || []),
                                    comment as TaskComment,
                                  ],
                                  whatHasChanged: ["comments"],
                                } as Task)
                          )
                        }
                        onRemoveComment={(comments) =>
                          setTaskToEdit((state) =>
                            state
                              ? {
                                  ...state,
                                  comments: comments as TaskComment[],
                                  whatHasChanged: [...(state.whatHasChanged || []), "comments"],
                                }
                              : ({
                                  comments: comments as TaskComment[],
                                  whatHasChanged: ["comments"],
                                } as Task)
                          )
                        }
                      />
                    </GeneralWrapper>
                  </>
                </OpenInfoSection>
              )}
              {openInfo && editEnabled && (
                <ButtonsContainer>
                  <ActionButtons
                    onCancel={() => {
                      setEditEnabled(false);
                      setErrors([]);
                      setIsSubmitted(false);
                      setTaskToEdit(task);
                    }}
                    customColorCancel={Color.TEXT_INFO}
                    onSave={() => {
                      handleEditTask();
                    }}
                    saveDisabled={!!errors.length}
                  />
                </ButtonsContainer>
              )}
            </Grid>
          </TaskItemContainer>
        </>
      )}
      {renderTimeControlModal()}
    </section>
  );
};

export default TaskItem;
